import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import * as dayjs from 'dayjs';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/gantt';
import { Subscription } from 'rxjs';

interface Tenure {
  name: string;
  jobs: any[];
  started_at: string;
  ended_at: string | null;
}

@Component({
    selector: 'app-person-job-history-gantt',
    templateUrl: './person-job-history-gantt.component.html',
    styleUrls: ['./person-job-history-gantt.component.scss'],
    standalone: false
})
export class PersonJobHistoryGanttComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  @Input() jobs: any[];
  Highcharts: typeof Highcharts = Highcharts;
  chartId = 'gantt-container-' + Math.random().toString(36).substring(2, 15);
  chart: Highcharts.Chart | undefined;

  private darkModeSubscription: Subscription;

  constructor() {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.generateGanttData();
  }

  ngOnDestroy(): void {
    if (this.darkModeSubscription) {
      this.darkModeSubscription.unsubscribe();
    }
    if (this.chart) {
      this.chart.destroy();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobs && changes.jobs.currentValue) {
      this.generateGanttData();
    }
  }

  generateGanttData() {
    if (!this.jobs || this.jobs.length === 0 || !document.getElementById(this.chartId)) {
      return;
    }

    // This logic is from inspect.component.ts to group jobs into tenures
    const tenureData = this.jobs
      .filter((job: any) => job.started_at && job.company.name !== 'None')
      .reduceRight((acc: any, job: any) => {
        let existingTenure = acc.find((t: any) => t.name === job.company.name && !t.ended_at);
        if (!existingTenure) {
          existingTenure = {
            name: job.company.name,
            jobs: [],
            started_at: job.started_at,
            ended_at: null,
          };
          acc.push(existingTenure);
        }

        existingTenure.jobs.push(job);

        if (job.is_first_at_company) {
          existingTenure.started_at = job.started_at;
        }
        if (job.is_last_at_company) {
          existingTenure.ended_at = job.ended_at;
        }

        return acc;
      }, []);

    // Sort tenures to show most recent first
    tenureData.sort((a: Tenure, b: Tenure) => {
      const aEndDate = a.ended_at ? new Date(a.ended_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bEndDate = b.ended_at ? new Date(b.ended_at).getTime() : Number.MAX_SAFE_INTEGER;
      if (aEndDate !== bEndDate) {
        return bEndDate - aEndDate;
      }
      return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
    });

    const ganttCategories = tenureData.map((t: any) => t.name);
    const ganttData: any[] = [];

    tenureData.forEach((tenure: any, i: number) => {
      tenure.jobs.forEach((job: any) => {
        ganttData.push({
          y: i,
          name: job.title,
          start: new Date(job.started_at).getTime(),
          end: job.ended_at ? new Date(job.ended_at).getTime() : new Date().getTime(),
        });
      });
    });

    const earliestJob = this.jobs
      .filter((j) => j.started_at)
      .reduce((earliest, current) => {
        if (!earliest || !earliest.started_at) return current;
        return new Date(current.started_at) < new Date(earliest.started_at) ? current : earliest;
      }, this.jobs[0]);

    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }

    this.chart = Highcharts.ganttChart(this.chartId, {
      credits: { enabled: false },
      title: {
        text: undefined,
      },
      navigator: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      yAxis: {
        type: 'category',
        categories: ganttCategories,
        min: 0,
        max: ganttCategories.length > 0 ? ganttCategories.length - 1 : 0,
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
        gridLineWidth: 0.5,
      },
      xAxis: [
        {
          min:
            earliestJob && earliestJob.started_at
              ? dayjs(earliestJob.started_at).startOf('year').toDate().getTime()
              : undefined,
          max: dayjs().endOf('month').toDate().getTime(),
        },
      ],
      tooltip: {
        enabled: true,
        pointFormat: '<b>{point.name}</b><br/>{point.start:%b %Y} - {point.end:%b %Y}',
      },
      plotOptions: {
        gantt: {
          dataLabels: {
            enabled: true,
            align: 'left',
            format: '{point.name}',
            style: {
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#000000',
              textOverflow: 'ellipsis',
              textOutline: '2px #ffffff',
            },
          },
          pointPadding: 0.2,
          groupPadding: 0.0,
          borderWidth: 0,
        },
      },
      series: [
        {
          name: 'Jobs',
          type: 'gantt',
          data: ganttData,
        },
      ],
      chart: {
        height: ganttCategories.length * 40 + 60, // dynamic height
      },
    });
  }
}
